import React, { InputHTMLAttributes } from 'react'
import styled, { css } from 'styled-components'

import Form from 'components/Form'
import UnstyledCaption from 'components/Caption'
import Box from 'components/Box'

const RadioInput = styled.div`
  display: inline-block;
  position: relative;
  vertical-align: middle;
  margin-right: 12px;
  cursor: pointer;
  width: 14px;
  height: 14px;

  border-radius: 50%;

  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-shadow: inset ${({ theme }) => theme.shadows.boxShadow.subtle};

  &:after {
    content: '';
    display: block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: white;

    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    opacity: 0;
  }
`

const HiddenInput = styled.input.attrs<{ type: string }>({
  type: 'radio',
})(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    cursor: pointer;

    &:checked + ${RadioInput} {
      border-color: ${theme.colors.primaryTint[100]};
      background-color: ${theme.colors.primaryTint[100]};

      &:after {
        opacity: 1;
      }
    }

    &:disabled + ${RadioInput} {
      opacity: 0.8;
    }
  `
)

type ContainerProps = {
  disabled?: boolean
}

const Container = styled(Form.Label)<ContainerProps>`
  display: flex;
  position: relative;
  align-items: flex-start;

  opacity: ${props => (props.disabled ? '0.5' : '1')};
`

const Label = styled.span(
  ({ theme }) => css`
    font-size: ${theme.fontSizes.smaller};
    color: ${theme.colors.foreground.secondary[2]};
    line-height: 1.4;
  `
)

const Caption = styled(UnstyledCaption)(
  ({ theme }) => css`
    font-size: ${theme.fontSizes.little};
    color: ${theme.colors.foreground.secondary[4]};
    line-height: 1.3;
    margin: 0;
  `
)

export interface RadioButtonProps
  extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  help?: string
  disabled?: boolean
  className?: string
}

const RadioButtonBase = styled(
  ({ label, help, className, ...props }: RadioButtonProps) => (
    <Container disabled={props.disabled} className={className}>
      <Box>
        <HiddenInput {...props} />
        <RadioInput />
      </Box>
      <Box>
        <Label>{label}</Label>
        {help && <Caption>{help}</Caption>}
      </Box>
    </Container>
  )
)``

const RadioButton = Object.assign(RadioButtonBase, {
  Input: RadioInput,
  Label: Label,
})

export default RadioButton
